import React, { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import "../CSS/CustomerChat.css";
import logo from "../assets/logo.png";
import Message from "./Message";
import MessageDetails from "./MessageDetails";
import Auth from "./Auth";
import ChatList from "./ChatList";
import Draggable from "react-draggable";
import axios from "axios";
import Header from "./Header";
import Footer from "./Footer";
import useContinuousTokenChecking from "../utils/continuousTokenChecking";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import LandingWindow from "./LandingWindow";
import LiveChat from "./LiveChat";
import Bot from "./Bot";
import AIAgentInfo from "./AIAgentInfo";
import MetricsAndAttributes from "./MetricsAndAttributes";
import CDAInfo from "./CDAInfo";
import SupportContent from "./SupportContent";
import Help from "./Help";
import DexKorOverview from "./DexKorOverview";
import DexKor from "./DexKor";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import "../CSS/Transitions.css";
import Convo from "./Convo";
import { useDispatch, useSelector } from "react-redux";
import {
  updateConvoMessage,
  updateConvoStatus,
} from "../Redux/Actions/convoActions";

const socket = io(`${process.env.REACT_APP_CHAT_SERVER}`);

const ChatWindow = (props) => {
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false); // State to track expansion
  const [attachmentType, setAttachmentType] = useState(null);
  const [view, setView] = useState("LandingWindow");
  const [token, setToken] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [ticketId, setTicketId] = useState(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const isDragging = useRef(false);
  const clickStartTime = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [defaultMessagesData, setDefaultMessagesData] = useState(null);
  const [setupDetails, SetSetupDetails] = useState(null);
  const [distanceMoved, setDistanceMoved] = useState("");
  const [activeIcon, setActiveIcon] = useState("home");
  const [isAnimating, setIsAnimating] = useState(false);
  const dispatch = useDispatch();
  const convo = useSelector((state) => state.convo);

  // Effect to set expanded state when navigating to specific components
  useEffect(() => {
    if (
      view === "AIAgentInfo" ||
      view === "MetricsAndAttributes" ||
      view === "CDAInfo" ||
      view === "SupportContent" ||
      view === "DexKor"
    ) {
      setIsExpanded(true);
    }
  }, [view]);

  const handleAnimationEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  useContinuousTokenChecking();

  const handleStart = () => {
    isDragging.current = true;
    clickStartTime.current = Date.now();
  };

  const handleStop = (e, data) => {
    setPosition({ x: data.x, y: data.y });
    isDragging.current = false;
    const movedDistance =
      Math.abs(data.x - position.x) + Math.abs(data.y - position.y);
    const clickDuration = Date.now() - clickStartTime.current;
    setDistanceMoved(movedDistance);

    if (clickDuration < 1000 && movedDistance < 1 && !isOpen) {
      setIsOpen(true);
    }
  };

  useEffect(() => {
    const getPartnerSetupDetails = async () => {
      if (!props) return;
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_CHAT_MICROSERVICE_URL}/partners/setup/${props.uniquePartnerId}`
        );
        SetSetupDetails(response.data.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching setup details:", error);
        setLoading(false);
      }
    };

    getPartnerSetupDetails();
  }, [props]);

  useEffect(() => {
    const fetchConversations = async () => {
      if (!customerDetails) return;
      setLoading(true);
      try {
        const customerId = customerDetails._id;

        const response = await axios.get(
          `${process.env.REACT_APP_CHAT_MICROSERVICE_URL}/conversations/${customerId}`
        );

        setConversations(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching conversations:", error);
        setLoading(false);
      }
    };

    fetchConversations();
  }, [customerDetails]);

  useEffect(() => {
    setToken(localStorage.getItem("dexkor-chat-widget-auth-token"));
    setCustomerDetails(
      JSON.parse(localStorage.getItem("dexkor-chat-widget-customer-details"))
    );
  }, []);

  useEffect(() => {
    socket.on("message", (message) => {
      console.log({ message });
      setConversations((prevConversations) =>
        prevConversations.map((conversation) =>
          conversation.roomId === message.room
            ? {
                ...conversation,
                messages: [...conversation.messages, message],
              }
            : conversation
        )
      );

      if (
        selectedConversation &&
        message.room === selectedConversation.roomId
      ) {
        setSelectedConversation((prevSelectedConversation) => ({
          ...prevSelectedConversation,
          messages: [...prevSelectedConversation.messages, message],
        }));
      }
    });

    socket.on("ticketStatusUpdated", (response) => {
      setConversations((prevConversations) =>
        prevConversations.map((conversation) =>
          conversation.roomId === response.roomId
            ? {
                ...conversation,
                ticketId: {
                  ...conversation.ticketId,
                  status: response.status,
                },
              }
            : conversation
        )
      );

      if (
        selectedConversation &&
        response.roomId === selectedConversation.roomId
      ) {
        setSelectedConversation((prevSelectedConversation) => ({
          ...prevSelectedConversation,
          ticketId: {
            ...prevSelectedConversation.ticketId,
            status: response.status,
          },
        }));
      }
    });

    return () => {
      socket.off("message");
      socket.off("ticketStatusUpdated");
    };
  }, [selectedConversation]);

  useEffect(() => {
    socket.on("connect", () => {
      const roomIds = conversations.map((conversation) => conversation.roomId);
      socket.emit("join", roomIds);
    });
  }, [selectedConversation]);

  useEffect(() => {
    if (attachment && attachmentType) {
      sendMessage();
    }
  }, [attachment, attachmentType]);

  const sendMessage = () => {
    const inputMessage = message.trim();
    if (inputMessage || (attachment && attachmentType)) {
      const msg = {
        room: selectedConversation.roomId,
        sender: "Customer",
        senderId: customerDetails._id,
        message: inputMessage === "" ? null : inputMessage,
        attachment,
        attachmentType,
      };
      socket.emit("message", msg);
      setMessage("");
      setAttachment(null);
      setAttachmentType(null);
    }
  };

  const [processedAttachments, setProcessedAttachments] = useState([]);

  useEffect(() => {
    const sendAttachments = async () => {
      if (defaultMessagesData?.attachment) {
        for (const attachment of defaultMessagesData.attachments) {
          // Create a unique identifier for each attachment
          const attachmentKey = `${attachment.name}-${attachment.size}`;

          // Check if this attachment has already been processed
          if (processedAttachments.includes(attachmentKey)) {
            console.log(
              `Attachment ${attachment.name} already sent, skipping.`
            );
            continue; // Skip if already processed
          }

          const reader = new FileReader();

          await new Promise((resolve) => {
            reader.onloadend = async () => {
              // Set attachment state
              setAttachment(reader.result); // Data URL
              setAttachmentType(attachment.type);

              // Wait for sendMessage to complete
              await sendMessage();

              // Mark the attachment as processed after the message is sent
              setProcessedAttachments((prev) => [...prev, attachmentKey]);

              resolve(); // Resolve after the message is sent
            };
            reader.readAsDataURL(attachment);
          });
        }

        // Reset the default messages data
        setDefaultMessagesData(null);
      }
    };

    sendAttachments(); // Call the function to process attachments
  }, [defaultMessagesData]); // Empty dependency array ensures this runs only once

  const createNewChat = async (formData) => {
    const roomId = `Room-${new Date().getTime()}`;
    const customerId = customerDetails._id;
    const uniquePartnerId = props.uniquePartnerId;
    socket.emit(
      "createRoom",
      uniquePartnerId,
      roomId,
      ticketId,
      customerId,
      async (response) => {
        if (response.success) {
          setConversations((prevConversations) => [
            ...prevConversations,
            response.conversation,
          ]);

          setSelectedConversation(response.conversation);
          setView("message");
          setDefaultMessagesData(formData);
        }
      }
    );
    setTicketId(null);
  };

  return (
    <Draggable
      position={position}
      onStart={handleStart}
      onStop={handleStop}
      cancel=".non-draggable"
    >
      <div className={`dexkor-chat-container ${isExpanded ? "expanded" : ""}`}>
        <div
          className={`dexkor-chat-widget transition-container ${
            isOpen ? "open" : ""
          } ${isExpanded ? "expanded" : ""} non-draggable`}
        >
          {isOpen &&
            view !== "LandingWindow" &&
            view !== "Bot" &&
            view !== "Convo" &&
            view !== "LiveChat" &&
            view !== "Help" &&
            view !== "DexKorOverview" && (
              <SwitchTransition mode="out-in">
                <CSSTransition
                  key={view}
                  timeout={500}
                  classNames="fade"
                  unmountOnExit
                >
                  {(state) => (
                    <Header
                      setIsOpen={setIsOpen}
                      view={view}
                      setView={setView}
                      setIsExpanded={setIsExpanded}
                      isExpanded={isExpanded}
                      logo={setupDetails?.widgetCustomizations.logoUrl}
                      setupDetails={setupDetails}
                      setActiveIcon={setActiveIcon}
                    />
                  )}
                </CSSTransition>
              </SwitchTransition>
            )}

          <SwitchTransition mode="out-in">
            <CSSTransition
              key={view}
              timeout={500}
              classNames="fade"
              unmountOnExit
            >
              {(state) => (
                <>
                  {view === "LandingWindow" ? (
                    <LandingWindow
                      setView={setView}
                      setupDetails={setupDetails}
                      setIsOpen={setIsOpen}
                      setActiveIcon={setActiveIcon}
                    />
                  ) : view === "Bot" ? (
                    <Bot
                      setView={setView}
                      setActiveIcon={setActiveIcon}
                      setupDetails={setupDetails}
                    />
                  ) : view === "Convo" ? (
                    <Convo
                      setView={setView}
                      setActiveIcon={setActiveIcon}
                      setupDetails={setupDetails}
                    />
                  ) : view === "LiveChat" ? (
                    <LiveChat setView={setView} setActiveIcon={setActiveIcon} />
                  ) : view === "AIAgentInfo" ? (
                    <AIAgentInfo />
                  ) : view === "MetricsAndAttributes" ? (
                    <MetricsAndAttributes />
                  ) : view === "CDAInfo" ? (
                    <CDAInfo />
                  ) : view === "SupportContent" ? (
                    <SupportContent />
                  ) : view === "Help" ? (
                    <Help
                      setView={setView}
                      setActiveIcon={setActiveIcon}
                      setupDetails={setupDetails}
                    />
                  ) : view === "DexKorOverview" ? (
                    <DexKorOverview
                      setView={setView}
                      setupDetails={setupDetails}
                    />
                  ) : view === "DexKor" ? (
                    <DexKor setView={setView} />
                  ) : view === "MessageDetails" ? (
                    <MessageDetails
                      selectedConversation={selectedConversation}
                      goBack={() => setView("chatList")}
                      setView={setView}
                      setupDetails={setupDetails}
                    />
                  ) : (
                    <>
                      {token === null && view === "chatList" && (
                        <Auth
                          isOpen={isOpen}
                          setToken={setToken}
                          setCustomerDetails={setCustomerDetails}
                          setupDetails={setupDetails}
                          setView={setView}
                          setActiveIcon={setActiveIcon}
                        />
                      )}
                      {token !== null && (
                        <>
                          {view === "chatList" ? (
                            <ChatList
                              setTicketId={setTicketId}
                              ticketId={ticketId}
                              setSelectedConversation={setSelectedConversation}
                              setView={setView}
                              createNewChat={createNewChat}
                              conversations={conversations}
                              isExpanded={isExpanded}
                              loading={loading}
                              setLoading={setLoading}
                              widgetConfigs={props}
                              setupDetails={setupDetails}
                            />
                          ) : (
                            view === "message" && (
                              <Message
                                selectedConversation={selectedConversation}
                                setSelectedConversation={
                                  setSelectedConversation
                                }
                                setConversations={setConversations}
                                setMessage={setMessage}
                                message={message}
                                setAttachment={setAttachment}
                                setAttachmentType={setAttachmentType}
                                attachmentType={attachmentType}
                                isOpen={isOpen}
                                sendMessage={sendMessage}
                                goBack={() => setView("chatList")}
                                setupDetails={setupDetails}
                                setView={setView}
                                widgetConfigs={props}
                              />
                            )
                          )}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </CSSTransition>
          </SwitchTransition>

          <SwitchTransition mode="out-in">
            <CSSTransition
              key={view}
              timeout={500}
              classNames="fade"
              unmountOnExit
            >
              {(state) =>
                isOpen &&
                view !== "Bot" &&
                view !== "Convo" &&
                view !== "AIAgentInfo" &&
                view !== "MetricsAndAttributes" &&
                view !== "CDAInfo" &&
                view !== "SupportContent" &&
                view !== "DexKor" && (
                  <div className="mt-auto mb-0">
                    <Footer
                      setView={setView}
                      activeIcon={activeIcon}
                      setActiveIcon={setActiveIcon}
                      setupDetails={setupDetails}
                      view={view}
                      widgetConfigs={props}
                    />
                  </div>
                )
              }
            </CSSTransition>
          </SwitchTransition>
        </div>

        <div className="flex items-center justify-end">
          {isOpen ? (
            <div
              className="dexkor-chat-minize-icon-container"
              onClick={() => {
                const clickDuration = Date.now() - clickStartTime.current;
                if (clickDuration < 1000 && distanceMoved < 1 && isOpen) {
                  setIsAnimating(true); // Start animation
                  setIsOpen(false); // Close the chat after animation
                }
              }}
            >
              <div
                className={`dexkor-chat-toggle-icon ${
                  isAnimating ? "slide-out" : ""
                }`}
                style={{
                  background: setupDetails?.widgetCustomizations.primaryColor,
                }}
                onAnimationEnd={handleAnimationEnd}
              >
                <ChevronDownIcon className="text-white" />
              </div>
              <div
                className="dexkor-chat-toggle-icon-polygon"
                style={{
                  borderBottom: `13px solid ${
                    setupDetails?.widgetCustomizations.primaryColor || "#2a4dad"
                  }`,
                }}
              ></div>
            </div>
          ) : (
            <div className="dexkor-chat-toggle-icon-container">
              <div
                className={`dexkor-chat-toggle-icon ${
                  isOpen ? "slide-out" : "slideIn"
                }`}
                style={{
                  background: setupDetails?.widgetCustomizations.primaryColor,
                }}
                onAnimationEnd={handleAnimationEnd}
              >
                <img
                  src={setupDetails?.widgetCustomizations.logoUrl
                    .replace(
                      "https://drive.google.com/file/d/",
                      "https://drive.google.com/uc?export=view&id="
                    )
                    .replace("/view?usp=sharing", "")}
                  alt="Chat"
                  draggable="false"
                />
              </div>
              <div
                className="dexkor-chat-toggle-icon-polygon"
                style={{
                  borderBottom: `13px solid ${
                    setupDetails?.widgetCustomizations.primaryColor || "#2a4dad"
                  }`,
                }}
              ></div>
            </div>
          )}
        </div>
      </div>
      {/* testing the pr */}
    </Draggable>
  );
};

export default ChatWindow;
